import React, { useCallback, useEffect, useState } from 'react';

import AutoComplete from '~/components/core/Molecules/Fields/AutoComplete';
import type { AutocompleteOption } from '~/hooks/useAutocomplete';

export interface AutocompleteFieldProps<T extends AutocompleteOption> {
  id: string;
  label: string;
  value: string; // Display value (name)
  onChange: (id: string | number, name: string) => void;
  options: T[];
  loading?: boolean;
  onInputChange?: (searchTerm: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

export function AutocompleteField<T extends AutocompleteOption>({
  id,
  label,
  value,
  onChange,
  options,
  loading = false,
  onInputChange,
  className,
  disabled = false,
  placeholder,
  error = false,
  helperText,
}: AutocompleteFieldProps<T>) {
  const [inputValue, setInputValue] = useState(value);

  // Sync inputValue with value prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent | null, newInputValue: string) => {
      setInputValue(newInputValue);
      if (onInputChange) {
        onInputChange(newInputValue);
      }
    },
    [onInputChange]
  );

  const handleChange = useCallback(
    (event: React.SyntheticEvent | undefined, selectedValue?: T | null) => {
      if (selectedValue && typeof selectedValue === 'object' && selectedValue.id) {
        // User selected from dropdown
        onChange(selectedValue.id, selectedValue.name || '');
        setInputValue(selectedValue.name || '');
      } else {
        // User cleared the field
        onChange('', '');
        setInputValue('');
      }
    },
    [onChange]
  );

  // Find the current selected option or create empty option for display
  const selectedOption =
    options.find((option) => option.name === inputValue) ||
    ({
      id: '',
      name: inputValue || '',
    } as T);

  return (
    <AutoComplete<T>
      id={id}
      label={label}
      options={options}
      value={selectedOption}
      getOptionLabel={(option) => option?.name || ''}
      getOptionSelected={(option, value) => {
        if (!option || !value) return false;
        // Handle empty state comparison
        if (!option.id && !value.id) return option.name === value.name;
        return option.id === value.id && option.name === value.name;
      }}
      onChange={handleChange}
      onInputChange={handleInputChange}
      className={className}
      disabled={disabled}
      loading={loading}
      autoComplete={true}
      autoHighlight={true}
      clearOnBlur={true}
      textFieldProps={{
        placeholder,
        error,
        helperText,
      }}
    />
  );
}
